import clsx from 'clsx';
import { Ref } from 'react';

import { RotationsInfo, Match } from '../../types';

interface MlpScoreSheetPrintProps {
	rotations: RotationsInfo;
	matches: Match[];
	innerRef?: Ref<HTMLDivElement>;
	sheetSize?: 'Full Sheets' | 'Half Sheets';
	teamOneTitle?: string;
	teamTwoTitle?: string;
	shouldDisplayPlayerNames?: {
		showHomeLineup: boolean;
		showAwayLineup: boolean;
	};
}

const emptyRotations: RotationsInfo = { matchUuid: '', teamOne: [], teamTwo: [] };

const ScoreSheetHeader = ({ teamName }: { teamName?: string; players?: string[] }) => (
	<div className="flex w-full items-start">
		<div className="w-[30%] ms-4">
			<div className="text-sm font-medium mb-2">Game ID:</div>
		</div>

		<div className="w-[40%] pr-4 flex flex-row">
			<div className="text-sm font-medium mb-2 ">TEAM:</div>
			{teamName ? <p className="border-b-[1.5px] border-black mb-1 text-sm font-medium w-full">{teamName}</p> : null}
		</div>

		<div className="w-[30%] flex justify-center gap-6">
			<div className="flex flex-col gap-3">
				<div className="text-center">
					<div className="text-xs font-bold">Serve</div>
				</div>
				<div className="text-center">
					<div className="text-xs font-bold">Receive</div>
				</div>
			</div>

			<div className="flex flex-col gap-3">
				<div className="text-center">
					<div className="text-xs font-bold">Left</div>
				</div>
				<div className="text-center">
					<div className="text-xs font-bold">Right</div>
				</div>
			</div>
		</div>
	</div>
);

const PlayerInput = ({ showMT = true, checkboxSize = 'w-4 h-4', playerName }: { showMT?: boolean; checkboxSize?: string; playerName?: string }) => (
	<div className="flex-1">
		<div className="flex items-center gap-4 mb-2">
			<span className="text-sm font-medium">Player:</span>
			<p className="flex-1 border-b border-black h-6">{playerName}</p>
		</div>
		<div className="flex items-center justify-between">
			<div className="flex items-center gap-2">
				<span className={showMT ? 'text-sm' : 'text-xs'}>Re-Serve</span>
				<div className={`${checkboxSize} border border-black`} />
			</div>
			<div className="flex-1 flex justify-center">{showMT ? <span className="text-sm font-bold">MT</span> : null}</div>
			<div className="w-16" />
		</div>
	</div>
);

const TieBreakerPlayerInput = ({ playerName }: { playerName?: string }) => (
	<div className="flex-1">
		<p className="border-b border-black h-6 mb-2">{playerName}</p>
		<div className="flex items-center gap-2">
			<span className="text-xs">Re-Serve</span>
			<div className="w-3 h-3 border border-black" />
		</div>
	</div>
);

const PlayerSection = ({
	isTieBreaker,
	isDoubles,
	playerOneName,
	playerTwoName,
	tieBreakerRotationsInfo
}: {
	isTieBreaker: boolean;
	isDoubles: boolean;
	playerOneName?: string;
	playerTwoName?: string;
	tieBreakerRotationsInfo?: RotationsInfo['teamOne'];
}) => (
	<div className={clsx(isTieBreaker ? 'w-full' : 'w-4/5 mx-auto', 'mt-3')}>
		{isTieBreaker ? (
			/* Tie Breaker - Four Players */
			<div>
				<div className="flex items-start gap-4 mb-3 mx-4">
					<span className="text-sm font-medium">Players:</span>
					<div className="flex-1 flex gap-2">
						<TieBreakerPlayerInput playerName={tieBreakerRotationsInfo?.[0]?.playerOneName} />
						<TieBreakerPlayerInput playerName={tieBreakerRotationsInfo?.[1]?.playerOneName} />
						<TieBreakerPlayerInput playerName={tieBreakerRotationsInfo?.[2]?.playerOneName} />
						<TieBreakerPlayerInput playerName={tieBreakerRotationsInfo?.[3]?.playerOneName} />
					</div>
				</div>
			</div>
		) : isDoubles ? (
			/* Doubles - Two Players */
			<div className="flex gap-8">
				<PlayerInput playerName={playerOneName} />
				<PlayerInput playerName={playerTwoName} />
			</div>
		) : (
			/* Singles - One Player */
			<div className="w-1/2 mx-auto">
				<PlayerInput playerName={playerOneName} />
			</div>
		)}
	</div>
);

const ScoreBoxes = ({ isRallyScoring, maxScore = 11 }: { isRallyScoring: boolean; maxScore?: number }) => {
	const totalBoxes = maxScore + 4; // Max score boxes + 4 additional boxes
	const switchEndsPosition = Math.ceil(maxScore / 2); // Calculate switch ends position

	return (
		<div className="flex items-start gap-4 w-3/4 mx-auto mt-5">
			<span className="text-sm font-medium w-16">{isRallyScoring ? 'Rally' : 'Sideout'}</span>
			<div className="flex-1">
				<div className="flex gap-0 border-l border-t border-b border-black flex-1">
					{Array.from({ length: totalBoxes }, (_, i) => i + 1).map((number) => {
						const isMaxScoreBox = number === maxScore;
						return (
							<div
								key={number}
								className={`flex-1 max-w-9 h-6 flex items-center justify-center text-sm font-medium border-r border-black ${
									isMaxScoreBox && 'border-b-2'
								}`}
							>
								{number}
							</div>
						);
					})}
				</div>

				{/* Switch ends triangle marker */}
				<div className="flex gap-0 flex-1">
					{Array.from({ length: totalBoxes }, (_, i) => i + 1).map((number) => {
						const isSwitchEndsPosition = number === switchEndsPosition;
						return (
							<div key={`marker-${number}`} className="flex-1 max-w-9 h-4 flex items-center justify-center">
								{!!isSwitchEndsPosition && (
									<svg width="16" height="16" viewBox="0 0 16 16" className="fill-none">
										<polygon points="8,2 14,14 2,14" stroke="black" strokeWidth="1" fill="none" />
									</svg>
								)}
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
};

const TimeoutViolations = ({ VCLength = 6 }: { VCLength?: number }) => (
	<div className="w-[85%] mx-auto mt-4">
		<div className="flex items-center justify-center gap-5">
			<div className="flex items-center gap-2">
				<span className="text-sm font-medium">TimeOut</span>
				<div className="w-6 h-5 border border-black" />
			</div>

			<div className="flex items-center gap-2">
				<span className="text-sm font-medium">TW/Blue</span>
				<div className="w-6 h-5 border border-black" />
			</div>

			<div className="flex items-center gap-2">
				<span className="text-sm font-medium">TF/Orange</span>
				<div className="w-6 h-5 border border-black" />
			</div>

			<div className="flex items-center gap-2">
				<span className="text-sm font-medium">VC</span>
				<div className="flex gap-2">
					{Array.from({ length: VCLength }, (_, i) => i + 1).map((number) => (
						<div key={number} className="flex items-center">
							<span className="text-xs ms-2 me-0.5">{number}</span>
							<div className="w-7 h-6 border border-black" />
						</div>
					))}
				</div>
			</div>
		</div>
	</div>
);

const SingleMatch = ({
	match,
	scoreFormatGameMaxValues,
	teamOneTitle,
	teamTwoTitle,
	tieBreakerRotationsInfo,
	shouldDisplayPlayerNames
}: {
	match: Match;
	scoreFormatGameMaxValues: number[];
	teamOneTitle: string;
	teamTwoTitle: string;
	tieBreakerRotationsInfo: RotationsInfo;
	shouldDisplayPlayerNames?: {
		showHomeLineup: boolean;
		showAwayLineup: boolean;
	};
}) => {
	const isDoubles = match.formatTitle.toLowerCase().includes('doubles');
	let teamOnePlayerOneName = match.teamOnePlayerOneName;
	let teamOnePlayerTwoName = match.teamOnePlayerTwoName;
	let teamTwoPlayerOneName = match.teamTwoPlayerOneName;
	let teamTwoPlayerTwoName = match.teamTwoPlayerTwoName;
	let tieBreakerRotationsInfoTeamOne = tieBreakerRotationsInfo?.teamOne;
	let tieBreakerRotationsInfoTeamTwo = tieBreakerRotationsInfo?.teamTwo;

	if (shouldDisplayPlayerNames?.showAwayLineup === false) {
		teamTwoPlayerOneName = '';
		teamTwoPlayerTwoName = '';
		tieBreakerRotationsInfoTeamTwo = [];
	}
	if (shouldDisplayPlayerNames?.showHomeLineup === false) {
		teamOnePlayerOneName = '';
		teamOnePlayerTwoName = '';
		tieBreakerRotationsInfoTeamOne = [];
	}

	return (
		<div className="px-4 pt-4">
			<ScoreSheetHeader teamName={teamTwoTitle + ' (A)'} />
			<PlayerSection
				isTieBreaker={!!match.isTieBreaker}
				isDoubles={isDoubles}
				playerOneName={teamTwoPlayerOneName ?? ''}
				playerTwoName={teamTwoPlayerTwoName ?? ''}
				tieBreakerRotationsInfo={tieBreakerRotationsInfoTeamTwo}
			/>
			{Array.from({ length: match.scoreFormatMaxGames || 1 }, (_, gameIndex) => {
				const gameMaxScore = scoreFormatGameMaxValues[gameIndex] || 11;
				return <ScoreBoxes key={gameIndex} isRallyScoring={match.isRallyScoring} maxScore={gameMaxScore} />;
			})}
			<TimeoutViolations />
			<div className="w-full mx-auto my-3">
				<div className="border-t border-dashed border-black" />
			</div>

			{/* Rotated Section for Second Team */}
			<div className="rotate-180">
				<ScoreSheetHeader teamName={teamOneTitle + ' (H)'} />
				<PlayerSection
					isTieBreaker={!!match.isTieBreaker}
					isDoubles={isDoubles}
					playerOneName={teamOnePlayerOneName ?? ''}
					playerTwoName={teamOnePlayerTwoName ?? ''}
					tieBreakerRotationsInfo={tieBreakerRotationsInfoTeamOne}
				/>
				{Array.from({ length: match.scoreFormatMaxGames || 1 }, (_, gameIndex) => {
					const gameMaxScore = scoreFormatGameMaxValues[gameIndex] || 11;
					return <ScoreBoxes key={`rotated-${gameIndex}`} isRallyScoring={match.isRallyScoring} maxScore={gameMaxScore} />;
				})}
				<TimeoutViolations />
				<div className="w-fyll mx-auto my-4">
					<div className="border-t border-dashed border-black" />
				</div>
			</div>
		</div>
	);
};

export const MlpScoreSheetPrint = ({
	teamOneTitle,
	teamTwoTitle,
	matches,
	rotations,
	innerRef,
	sheetSize = 'Full Sheets',
	shouldDisplayPlayerNames
}: MlpScoreSheetPrintProps) => {
	return (
		<div ref={innerRef} className="m-0 p-0 hidden print:block font-sans bg-white text-black">
			{matches.map((match, index) => {
				const scoreFormatGameMaxValues = [
					match.scoreFormatGame1Max,
					match.scoreFormatGame2Max,
					match.scoreFormatGame3Max,
					match.scoreFormatGame4Max,
					match.scoreFormatGame5Max
				].filter((v) => typeof v === 'number');

				return (
					<div key={match.matchUuid || index} className={sheetSize === 'Half Sheets' ? 'print-half-sheet' : 'print-full-sheet'}>
						<SingleMatch
							match={match}
							scoreFormatGameMaxValues={scoreFormatGameMaxValues}
							teamOneTitle={teamOneTitle || ''}
							teamTwoTitle={teamTwoTitle || ''}
							tieBreakerRotationsInfo={rotations ?? emptyRotations}
							shouldDisplayPlayerNames={shouldDisplayPlayerNames}
						/>
					</div>
				);
			})}
		</div>
	);
};
